/**
 * ⌘K / Ctrl+K, from anywhere the chrome is drawn (brief 06, `KLQ-20260905-001` phase 5).
 *
 * One window listener, owned by the top bar that draws the search box — the bar is on every
 * signed-in screen, so "anywhere in the app" is "anywhere the bar is". Both modifiers on every
 * platform: a Mac user on a borrowed PC keyboard presses Ctrl, and neither combination does
 * anything else inside this app. `preventDefault` is what stops the browser's own ⌘K (the
 * address bar's search in several browsers) from taking the keystroke first.
 */

import { useEffect, useRef } from 'react';

export function useSearchShortcut(onOpen: () => void): void {
  // Through a ref, so the listener is added once rather than on every render of the bar.
  const open = useRef(onOpen);
  useEffect(() => {
    open.current = onOpen;
  }, [onOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      if (event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      open.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
